import { useTranslation } from 'react-i18next';
import useTypeEntity from '../../../useTypeEntity';
import React from 'react';
import { NormalizedGroup } from '../../../../../../../../redux/structure/GroupsAdapter';
import _ from 'lodash';
import { ITypeRequest } from '../../../../../../../../theme/ui/Dashboard/Dashboard.model';
import { WorkspaceAPIResponse } from '../../../../../../../../redux/workspace/WorkspaceAPI.model';
import WorkspaceAPI from '../../../../../../../../redux/workspace/WorkspaceAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import useMyWorkspace from '../../../../../../../../redux/workspace/hooks/useMyWorkspace';

export interface IUseCostPerKwhSettingProps {
  typeRequest: ITypeRequest;
  workspaceId?: string;
  groupId?: string;
}
export function useCostPerKwhSetting(props: IUseCostPerKwhSettingProps): {
  value: number | undefined | '';
  unit: string;
  unitString: string;
  error?: boolean;
  group?: NormalizedGroup;
  workspace?: WorkspaceAPIResponse;
  initialValue?: number | undefined | '';
  inheritValue?: boolean;
} {
  const { typeRequest, workspaceId } = props;
  const { entity, pathGroups } = useTypeEntity();
  const t = useTranslation().t as (key: string, options?: any) => string;
  const { data: workspaceSelected } = WorkspaceAPI.useGetWorkspaceByIdQuery(
    workspaceId ?? skipToken
  );

  const { data: myWorkspace } = useMyWorkspace();
  return React.useMemo(() => {
    switch (typeRequest) {
      case 'group': {
        const dataCurrentGroup = getDataFromGroup(entity, t);
        if (dataCurrentGroup) return dataCurrentGroup;

        const dataParentGroups = getDataFromParentGroups(pathGroups, t);
        if (dataParentGroups) return dataParentGroups;

        const dataWorkspace = getDataFromWorkspace(workspaceSelected, t);
        if (dataWorkspace) return dataWorkspace;

        const dataMyWorkspace = getDataFromWorkspace(myWorkspace, t);
        if (dataMyWorkspace) return dataMyWorkspace;
        break;
      }
      case 'workspace': {
        const dataWorkspace = getDataFromWorkspace(workspaceSelected, t, true);
        if (dataWorkspace) return dataWorkspace;

        const dataMyWorkspace = getDataFromWorkspace(myWorkspace, t);
        if (dataMyWorkspace) return dataMyWorkspace;
        break;
      }
      case 'user':
      case 'workspaces': {
        const dataMyWorkspace = getDataFromWorkspace(myWorkspace, t, true);
        if (dataMyWorkspace) return dataMyWorkspace;
        break;
      }
      default:
        break;
    }
    return {
      value: undefined,
      unit: DEFAULT_CURRENCY,
      unitString: t(`dashboard.energy_cost.currencies.${DEFAULT_CURRENCY}`, {
        defaultValue: null,
      }),
      error: true,
    };
  }, [typeRequest, t, entity, pathGroups, workspaceSelected, myWorkspace]);
}

function getDataFromGroup(
  group: NormalizedGroup | undefined,
  t: (key: string, options?: any) => string
) {
  const costCurrentGroup = getCostPerKwhSettingScope(group);
  if (costCurrentGroup)
    return {
      value: costCurrentGroup && parseFloat(costCurrentGroup),
      initialValue: costCurrentGroup && parseFloat(costCurrentGroup),
      unit: getCostPerKwhUnitSettingScope(group) ?? DEFAULT_CURRENCY,
      unitString: t(
        `dashboard.energy_cost.currencies.${
          getCostPerKwhUnitSettingScope(group) ?? DEFAULT_CURRENCY
        }`,
        { defaultValue: null }
      ),
    };
}

function getDataFromParentGroups(
  pathGroups: NormalizedGroup[],
  t: (key: string, options?: any) => string
) {
  const groupWithSetting = _.clone(pathGroups)
    .reverse()
    .find((group) => Boolean(getCostPerKwhSettingScope(group)));
  if (groupWithSetting) {
    const costPerKwh = getCostPerKwhSettingScope(groupWithSetting);
    const costPerKwhUnit = getCostPerKwhUnitSettingScope(groupWithSetting);
    return {
      value: costPerKwh && parseFloat(costPerKwh),
      unit: costPerKwhUnit ?? DEFAULT_CURRENCY,
      group: groupWithSetting,
      inheritValue: true,
      unitString: t(
        `dashboard.energy_cost.currencies.${
          costPerKwhUnit ?? DEFAULT_CURRENCY
        }`,
        { defaultValue: null }
      ),
    };
  }
}

function getDataFromWorkspace(
  workspace: WorkspaceAPIResponse | undefined,
  t: (key: string, options?: any) => string,
  initialValue?: boolean
) {
  // For workspace, look for the setting in the workspace settings
  const costPerKwh = getCostPerKwhSettingWorkspace(workspace);
  const costPerKwhUnit = getCostPerKwhUnitSettingWorkspace(workspace);
  if (costPerKwh) {
    return {
      value: costPerKwh && parseFloat(costPerKwh),
      unit: costPerKwhUnit ?? DEFAULT_CURRENCY,
      initialValue: initialValue
        ? costPerKwh && parseFloat(costPerKwh)
        : undefined,
      workspace,
      inheritValue: !initialValue,
      unitString: t(
        `dashboard.energy_cost.currencies.${
          costPerKwhUnit ?? DEFAULT_CURRENCY
        }`,
        { defaultValue: null }
      ),
    };
  }
}

const KEY_COST_PER_KWH = 'reportPowerPricePerKilowattHour';
export function getCostPerKwhSettingScope(group?: NormalizedGroup) {
  return group?.attributes?.[KEY_COST_PER_KWH];
}

export const DEFAULT_CURRENCY = 'USD';
const KEY_COST_PER_KWH_UNIT = 'reportPowerPriceCurrency';
export function getCostPerKwhUnitSettingScope(group?: NormalizedGroup) {
  return group?.attributes?.[KEY_COST_PER_KWH_UNIT];
}

export function getCostPerKwhSettingWorkspace(
  workspace?: WorkspaceAPIResponse
) {
  return workspace?.settings?.[KEY_COST_PER_KWH];
}
export function getCostPerKwhUnitSettingWorkspace(
  workspace?: WorkspaceAPIResponse
) {
  return workspace?.settings?.[KEY_COST_PER_KWH_UNIT];
}
